import type { Doc } from '@doc';
import { createEdgeMutation, createNodeMutation, insertUnitMutation } from '@doc';
import type { Point } from '@geom';
import { Elements, type Edges, type NID, type Nodes } from '@graph';
import { localeManager } from '@reactive';
import { StringId } from '@strings';
import { showNotification, SlVariant } from '@ui';
import { anyElement, expectDefined, fixedNumber } from '@utils';
import type { CommandContext } from './CommandContext';
import { revealBounds } from './zoom.commands';

export interface ConnectResult {
  doc: Doc;
  selection: Elements;
  entryPoints?: Map<NID, Point>;
  pan: Point;
  zoom: number;
}

export function connect(
  startPosition: Point,
  endPosition: Point,
  startElements: Elements,
  endElements: Elements,
  startGroup: NID | undefined,
  endGroup: NID | undefined,
  cls: string | undefined,
  context: CommandContext
): ConnectResult | undefined {
  let { doc, selection } = context;
  const { layouter } = context;

  const oldDoc = doc;

  try {
    const addedNodes: Nodes = new Set();
    const addedEdges: Edges = new Set();

    let entryPoints: Map<NID, Point> | undefined;

    function addEntryPoint(node: NID, point: Point) {
      if (!entryPoints) {
        entryPoints = new Map<NID, Point>();
      }
      entryPoints.set(node, point);
    }

    doc = doc.produce('connect', draft => {
      const doc = draft as Doc;
      startGroup = startGroup ?? anyElement(doc.groupsIn(startElements.nodes));
      endGroup = endGroup ?? anyElement(doc.groupsIn(endElements.nodes));

      let sourceUnits = doc.unitsIn(startElements.nodes);
      let sourceEdges = startElements.edges;

      let targetUnits = doc.unitsIn(endElements.nodes);
      let targetEdges = endElements.edges;

      const addSourceNode = sourceUnits.size === 0 && sourceEdges.size === 0;
      const addTargetNode = targetUnits.size === 0 && targetEdges.size === 0;

      const selectionUnits = doc.unitsIn(selection.nodes);

      const startElementsInSelection =
        sourceUnits.intersection(selectionUnits).size > 0 ||
        sourceEdges.intersection(selection.edges).size > 0;
      const endElementsInSelection =
        targetUnits.intersection(selectionUnits).size > 0 ||
        targetEdges.intersection(selection.edges).size > 0;

      if (addSourceNode) {
        const newNode = createNodeMutation(
          doc,
          cls,
          false,
          layouter.cache,
          startPosition,
          startGroup
        );
        sourceUnits.add(newNode);
        addedNodes.add(newNode);
        addEntryPoint(newNode, startPosition);
      }

      if (addTargetNode) {
        const newNode = createNodeMutation(
          doc,
          cls,
          false,
          layouter.cache,
          endPosition,
          endGroup
        );
        targetUnits.add(newNode);
        addedNodes.add(newNode);
        addEntryPoint(newNode, endPosition);
      }

      if ((addSourceNode && !addTargetNode) || endElementsInSelection) {
        targetUnits = targetUnits.union(selectionUnits);
        targetEdges = targetEdges.union(selection.edges);
      } else if ((!addSourceNode && addTargetNode) || startElementsInSelection) {
        sourceUnits = sourceUnits.union(selectionUnits);
        sourceEdges = sourceEdges.union(selection.edges);
      }

      for (const edge of sourceEdges) {
        const [source, target] = doc.graph.endpoints(edge);
        const newNodeParent =
          startGroup ?? doc.graph.forest.leastCommonAncestor(new Set([source, target]));
        const position = doc.edgeLayout(edge).path.pointAt(0.5);
        const { newNode, newEdge } = insertUnitMutation(
          doc,
          cls,
          position,
          edge,
          newNodeParent,
          layouter.cache
        );
        sourceUnits.add(newNode);
        addedNodes.add(newNode);
        addedEdges.add(newEdge);
      }

      for (const edge of targetEdges) {
        const [source, target] = doc.graph.endpoints(edge);
        const newNodeParent =
          endGroup ?? doc.graph.forest.leastCommonAncestor(new Set([source, target]));
        const position = doc.edgeLayout(edge).path.pointAt(0.5);
        const { newNode, newEdge } = insertUnitMutation(
          doc,
          cls,
          position,
          edge,
          newNodeParent,
          layouter.cache
        );
        targetUnits.add(newNode);
        addedNodes.add(newNode);
        addedEdges.add(newEdge);
      }

      for (const source of sourceUnits) {
        for (const target of targetUnits) {
          const edge = createEdgeMutation(doc, source, target);
          addedEdges.add(edge);
        }
      }
    });

    if (addedNodes.size === 0 && addedEdges.size === 0) {
      return;
    }

    const MAX_ADDED_ELEMENTS = 10;
    const addedCount = addedNodes.size + addedEdges.size;
    if (addedCount > MAX_ADDED_ELEMENTS) {
      const placeholders = {
        count: fixedNumber(addedCount),
        max: fixedNumber(MAX_ADDED_ELEMENTS)
      };
      throw new Error(
        localeManager.localizedString(StringId.ERROR_TOO_MANY_NODES, placeholders)
      );
    }

    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    selection = Elements.make(addedNodes, addedEdges);
    const { pan, zoom } = revealBounds(
      expectDefined(doc.elementsBounds(selection)),
      context
    );
    const result = { doc, selection, entryPoints, pan, zoom };
    context.update(result, true);
    return result;
  } catch (e) {
    console.warn(e);
    const message =
      e instanceof Error
        ? e.message
        : localeManager.localizedString(StringId.ERROR_GENERIC);
    void showNotification(message, { variant: SlVariant.WARNING });
    return;
  }
}
